import { ColumnDef } from '@tanstack/react-table';

type ColumnMeta = {
  hideWhenEmpty?: boolean;
};

/**
 * Filters out columns that have `meta.hideWhenEmpty: true` when all rows have no data for that column.
 */
export function getVisibleColumns<T>(columns: ColumnDef<T>[], data: T[]): ColumnDef<T>[] {
  // Get columns that need checking
  const columnsToCheck = columns.filter((col) => (col.meta as ColumnMeta)?.hideWhenEmpty);

  // If no columns need checking, return all
  if (columnsToCheck.length === 0) {
    return columns;
  }

  // If no data, hide all hideWhenEmpty columns
  if (!data || data.length === 0) {
    return columns.filter((col) => !(col.meta as ColumnMeta)?.hideWhenEmpty);
  }

  // Track which columns have data (by column id)
  const columnsWithData = new Set<string>();

  // Single pass through data
  for (const row of data) {
    for (const col of columnsToCheck) {
      const colId = col.id!;

      // Skip if already found data for this column
      if (columnsWithData.has(colId)) continue;

      let value: unknown;
      if ('accessorFn' in col && typeof col.accessorFn === 'function') {
        value = col.accessorFn(row, 0);
      } else if ('accessorKey' in col && col.accessorKey) {
        value = (row as Record<string, unknown>)[col.accessorKey as string];
      }

      // Check if value is not empty
      const hasValue =
        value !== null &&
        value !== undefined &&
        (!Array.isArray(value) || value.length > 0) &&
        (typeof value !== 'string' || value.trim() !== '');

      if (hasValue) {
        columnsWithData.add(colId);
      }
    }

    // Early exit: all columns found to have data
    if (columnsWithData.size === columnsToCheck.length) {
      break;
    }
  }

  // Filter columns based on results
  return columns.filter((col) => {
    const meta = col.meta as ColumnMeta;
    if (!meta?.hideWhenEmpty) return true;
    return columnsWithData.has(col.id!);
  });
}
